import { ChartType, ProjectChartType } from "@custom-types/analytics-types";
import { ProjectId } from "@custom-types/base-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  fetchCompanyStats,
  fetchProjectStats,
} from "@store/analytics/analytics-slice-thunk";
import { TIME_FRAMES, TimeFrameItem } from "@utils/time-utils";

/** State of analytics */
export interface AnalyticsState {
  /** The Id of the selected project to choose analytics */
  selectedAnalyticsProjectId: ProjectId | null;

  /** The selected time frame to limit all analytics */
  selectedAnalyticsTimeFrame: TimeFrameItem["value"];

  /** The stats of the company */
  companyStats: FaroCoreApiTypes.CompanyStatistic[] | null;

  /**
   * The stats of a single project
   * The key is the project id
   * The value is the stats belonging to that project.
   */
  projectStats: {
    [key: ProjectId]: FaroCoreApiTypes.CompanyStatistic[];
  };

  /** Collects all the fetching properties for this slice */
  fetching: {
    /** Indicates if company stats are currently being fetched from the backend */
    isFetchingCompanyStats: boolean;

    /** Indicates if stats for a project are currently being fetched from the backend */
    isFetchingProjectStats: boolean;
  };

  /** Collects properties used in the analytics chart displayed in the project overview page */
  projectOverview: {
    /** The selected time frame to limit the displayed chart */
    selectedTimeFrame: TimeFrameItem["value"];

    /** The selected project chart type to display */
    selectedChartType: ProjectChartType;
  };
}

/** The default time frame to show analytics in months */
export const DEFAULT_TIME_FRAME = 6;

/** Uses DEFAULT_TIME_FRAME to set the default value for the time frame  */
const DEFAULT_TIME_FRAME_VALUE =
  TIME_FRAMES.find((timeFrame) => timeFrame.value === DEFAULT_TIME_FRAME)
    ?.value ?? TIME_FRAMES[0].value;

const initialState: AnalyticsState = {
  selectedAnalyticsProjectId: null,
  // Use the default value if it exists in the list of time frames, otherwise use the first one
  selectedAnalyticsTimeFrame: DEFAULT_TIME_FRAME_VALUE,
  companyStats: null,
  projectStats: {},
  fetching: {
    isFetchingCompanyStats: false,
    isFetchingProjectStats: false,
  },
  projectOverview: {
    selectedTimeFrame: DEFAULT_TIME_FRAME_VALUE,
    selectedChartType: ChartType.projectSpheres,
  },
};

/**
 * Slice to access state of loaded groups
 */
const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    /**
     * Stores the ID of the selected project.
     */
    setSelectedAnalyticsProjectId(
      state,
      action: PayloadAction<ProjectId | null>
    ) {
      state.selectedAnalyticsProjectId = action.payload;
    },

    /**
     * Stores the selected time frame.
     */
    setSelectedAnalyticsTimeFrame(
      state,
      action: PayloadAction<TimeFrameItem["value"] | null>
    ) {
      state.selectedAnalyticsTimeFrame = action.payload ?? DEFAULT_TIME_FRAME;
    },

    /** Sets the selected project chart time frame */
    setSelectedProjectChartTimeFrame(
      state,
      action: PayloadAction<TimeFrameItem["value"]>
    ) {
      state.projectOverview.selectedTimeFrame = action.payload;
    },

    /** Sets the selected project chart type */
    setSelectedProjectChartType(
      state,
      action: PayloadAction<ProjectChartType>
    ) {
      state.projectOverview.selectedChartType = action.payload;
    },

    /** Resets the analytics store to its initial state. */
    resetAnalyticsState: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCompanyStats.pending, (state) => {
        state.fetching.isFetchingCompanyStats = true;
      })
      .addCase(fetchCompanyStats.fulfilled, (state, action) => {
        state.fetching.isFetchingCompanyStats = false;
        state.companyStats = action.payload;
      })
      .addCase(fetchCompanyStats.rejected, (state) => {
        state.fetching.isFetchingCompanyStats = false;
      })

      .addCase(fetchProjectStats.pending, (state) => {
        state.fetching.isFetchingProjectStats = true;
      })
      .addCase(fetchProjectStats.fulfilled, (state, action) => {
        state.fetching.isFetchingProjectStats = false;
        state.projectStats[action.payload.projectId] = action.payload.stats;
      })
      .addCase(fetchProjectStats.rejected, (state) => {
        state.fetching.isFetchingProjectStats = false;
      });
  },
});

export const {
  setSelectedAnalyticsProjectId,
  setSelectedAnalyticsTimeFrame,
  setSelectedProjectChartTimeFrame,
  setSelectedProjectChartType,
  resetAnalyticsState,
} = analyticsSlice.actions;

export const analyticsReducer = analyticsSlice.reducer;
